import { Component, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'atom-label',
  templateUrl: './label.component.html',
  styleUrls: ['./label.component.scss'],
})
export class LabelComponent {
  @Input() text: string | number = '';
  @Input() tooltipText?: string;

  constructor(private translateService: TranslateService) {}

  private getTranslation(key: string): string {
    const translationKey = `LABELS.${key}`;
    const translation = this.translateService.instant(translationKey);
    return translation === translationKey ? key : translation;
  }

  getTooltipTranslation(): string {
    if (!this.tooltipText) return '';

    // Separar el texto antes de los dos puntos y los números
    const [textPart, numberPart] = this.tooltipText.split(':');

    if (!textPart) return this.tooltipText;

    // Traducir solo la parte del texto
    const translatedText = this.translateService.instant('TOOLTIPS.' + textPart.trim());

    // Si no existe traducción, devolver el texto tal y como está
    const finalText = translatedText !== 'TOOLTIPS.' + textPart.trim() ? translatedText : textPart.trim();

    // Reconstruir el string con la traducción y los números originales
    return numberPart ? `${finalText}: ${numberPart}` : finalText;
}

  processTranslation(input: string | number): string {
    const inputString = String(input);

    // Caso 1: Textos que empiezan con "Ocupacion"
    if (inputString.toLowerCase().startsWith('ocupacion')) {
      const translationKey = `LABELS.${inputString}`;
      const fullTranslation = this.translateService.instant(translationKey);

      // Si existe una traducción completa, se retorna esa traducción
      if (fullTranslation !== translationKey) {
        return fullTranslation;
      }

      // Si no existe una traducción completa, se traduce solo la palabra "Ocupacion" y se conserva el resto del texto
      const occupationTranslated = this.getTranslation('Ocupacion');
      const dynamicText = inputString.substring(9).trim();

      return dynamicText ? `${occupationTranslated} ${dynamicText}` : occupationTranslated;
    }

    // Caso 2: Texto con porcentaje
    const percentageMatch = inputString.match(/^([^0-9%]*)?(\d+(?:\.\d+)?%)?(.*)$/);
    if (percentageMatch && percentageMatch[2]) {
      const beforeText = percentageMatch[1]?.trim() || '';
      const percentage = percentageMatch[2];
      const afterText = percentageMatch[3]?.trim() || '';

      const translatedBefore = beforeText ? this.getTranslation(beforeText) : '';
      const translatedAfter = afterText ? this.getTranslation(afterText) : '';

      if (!beforeText) return `${percentage} ${translatedAfter}`.trim();
      if (!afterText) return `${translatedBefore} ${percentage}`.trim();
      return `${translatedBefore} ${percentage} ${translatedAfter}`.trim();
    }

    // Caso 3: Texto con números
    const numberMatch = inputString.match(/^([^\d]*):?\s*(\d+(?:\.\d+)?)$/);
    if (numberMatch) {
      const textPart = numberMatch[1].trim();
      const numberPart = numberMatch[2];
      const translatedText = textPart ? this.getTranslation(textPart) : '';
      const separator = translatedText.endsWith('-') ? '' : ' ';
      return `${translatedText}${separator}${numberPart}`.trim();   // Si el texto traducido ya termina en guión, no se agrega espacio adicional
    }

    // Caso 4: Texto sin números ni porcentajes
    return this.getTranslation(inputString);
  }
}
